/**
 * CoreUp staff viewing this shop (docs/02 §2.3). The 60-minute token lives in sessionStorage only (gone when the tab
 * closes), is never put in localStorage / cookies / logs, and is sent as `X-Impersonation-Token` by lib/api.ts.
 * It authenticates on its own: the shop site's own session cookie (if any) is irrelevant while it is present.
 */
import { useSession } from '@/stores/session';

const KEY = 'coreup.impersonation';

export type ImpersonationSession = { token: string; tenantId: string };

/** What the CoreUp back office hands over in the URL fragment: the session plus the branch to open (optional). */
export type ImpersonationLanding = ImpersonationSession & { branchId: string | null };

/** Why a view-as-shop session is over; shown on `/impersonate?ended=…`. */
export type ImpersonationEnd = 'expired' | 'ended' | 'invalid' | 'exited';

const UUID = /^[0-9a-f-]{36}$/i;

export function getImpersonation(): ImpersonationSession | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<ImpersonationSession>;
    return typeof v.token === 'string' && typeof v.tenantId === 'string'
      ? { token: v.token, tenantId: v.tenantId }
      : null;
  } catch {
    return null;
  }
}

export function setImpersonation(session: ImpersonationSession): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    /* storage blocked: the session simply will not start */
  }
}

export function clearImpersonation(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Parse `#token=…&tenant=<uuid>[&branch=<uuid>]` from the URL fragment handed over by the CoreUp back office. */
export function parseImpersonationHash(hash: string): ImpersonationLanding | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const token = params.get('token');
  const tenantId = params.get('tenant');
  const branch = params.get('branch');
  if (!token || !tenantId || !UUID.test(tenantId)) return null;
  return { token, tenantId, branchId: branch && UUID.test(branch) ? branch : null };
}

/** Map the API's 401 `details.reason` to the notice shown when a view-as-shop session ends. */
export function impersonationEndFromDetails(details: unknown): ImpersonationEnd {
  const reason =
    typeof details === 'object' && details !== null && 'reason' in details
      ? (details as { reason: unknown }).reason
      : null;
  if (reason === 'impersonation_expired') return 'expired';
  if (reason === 'impersonation_ended') return 'ended';
  return 'invalid';
}

/** `HH:MM` (24h, the viewer's clock) of the moment the view-as-shop token stops working. */
export function formatImpersonationExpiry(expiresAt: string): string {
  return new Intl.DateTimeFormat('th-TH', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
    new Date(expiresAt),
  );
}

/** Drop the token and leave the shop for the "session over" notice (full load: nothing of the shop stays in memory). */
export function leaveImpersonation(ended: ImpersonationEnd): void {
  clearImpersonation();
  useSession.getState().reset();
  window.location.replace(`/impersonate?ended=${ended}`);
}
